import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Content, FilledContentRelationshipField } from '@prismicio/client';
import { ProductCatalogService } from '@yol-digital/ms-client';
import { LineCheckEligibleAccess, LineCheckInstallationAddress, LineCheckResponse } from 'interfaces';
import { LineCheckFormComponent } from 'line-check-form';
import { LineCheckProductsService } from 'line-check-products';
import { LoadingComponent } from 'loading';
import { PrismicLibService, prismicToHtmlString, prismicToImage } from 'prismic';
import { Product, ProductListItem, ProductService } from 'product-shared';
import { StorageKeys } from 'utils';
import { LineCheckResultListComponent } from './line-check-result-list/line-check-result-list.component';
import { LineCheckRepeatableItem } from './line-check.model';
import { LineCheckSlice } from './line-check.slice';

@Component({
  selector: 'app-line-check',
  templateUrl: './line-check.component.html',
  imports: [LineCheckFormComponent, LineCheckResultListComponent, AsyncPipe, LoadingComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineCheckComponent implements OnInit {
  public lineCheckProductsService = inject(LineCheckProductsService);
  private productService = inject(ProductService);
  private prismicLibService = inject(PrismicLibService);
  @ViewChild('lineCheckResultList') lineCheckResultListRef: ElementRef;
  @Input() slice: LineCheckSlice;
  lineCheckEligibleAccess: LineCheckEligibleAccess;
  lineCheckFormsHidden = signal(true);
  private productsFromPC: ProductCatalogService.CatalogResponse[];
  private specialProductCodes: string[];
  private productsFromPrismic: Content.ProductBoxDocumentData[];
  public filteredProducts: ProductListItem[];

  async ngOnInit() {
    await this.fetchProducts();
    const resp = await this.lineCheckProductsService.checkForAddressOnStorage(StorageKeys.LineCheckAddress);
    if (resp) {
      await this.handleLineCheckResponse(resp);
    }

    this.lineCheckFormsHidden.set(false);
  }

  public onAddressFormSubmitEvent(addressLineCheck: LineCheckInstallationAddress) {
    this.lineCheckProductsService.addressPrefill$.next(addressLineCheck);
    this.lineCheckProductsService.performAddressLineCheck(addressLineCheck).subscribe(async resp => {
      this.lineCheckProductsService.lineCheckResponse$.next(resp);
      if (resp) {
        this.lineCheckProductsService.saveLineCheckAddressToStorage(
          resp.installationAddress,
          StorageKeys.LineCheckAddress
        );
        await this.handleLineCheckResponse(resp);
      }
    });
  }

  public onFormReset() {
    this.lineCheckProductsService.resetLineCheckForm();
  }

  public async handleLineCheckResponse(newResp: LineCheckResponse) {
    this.lineCheckProductsService.addLineCheckInfoToDatalayer(newResp);

    this.lineCheckEligibleAccess = this.lineCheckProductsService.extractEligibleAccessByAddress(
      newResp,
      this.productsFromPC.map(product => new Product(product))
    );
    await this.showProducts(newResp.refId);
  }

  private async showProducts(lineCheckId?: string) {
    const hasEligibleProduct = this.slice.special_product_type
      ? this.lineCheckProductsService.hasEligibleSpecialProduct(this.lineCheckEligibleAccess)
      : this.lineCheckProductsService.hasEligibleProduct(this.slice.product_type, this.lineCheckEligibleAccess);
    this.lineCheckProductsService.hasEligibleProduct$.next(hasEligibleProduct);

    if (!this.slice.type_of_page_shown_on) {
      this.prismicConfigurationErrorHandler('"Type of page shown on" not defined');
      return;
    }

    if (this.slice.type_of_page_shown_on === 'special product detail page') {
      (this.filteredProducts as ProductListItem<Product>[]) = await this.fetchSpecialProducts(lineCheckId);
    } else {
      if (hasEligibleProduct) {
        (this.filteredProducts as ProductListItem<Product>[]) = await this.fetchEligibleProducts(lineCheckId);
      } else if (this.slice.type_of_page_shown_on === 'home page') {
        (this.filteredProducts as ProductListItem<Product>[]) =
          await this.fetchHomePageAlternativeProducts(lineCheckId);
      } else if (this.slice.type_of_page_shown_on === 'detail page') {
        (this.filteredProducts as ProductListItem<Product>[]) =
          await this.fetchDetailPageAlternativeProducts(lineCheckId);
      }
    }
    this.lineCheckProductsService.lineCheckResultsShown$.next(true);
    if (lineCheckId) this.scrollToResults();
  }

  private scrollToResults() {
    setTimeout(() => {
      this.lineCheckResultListRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  }

  private async fetchSpecialProducts(lineCheckId: string) {
    if (!this.slice.product_code) {
      this.prismicConfigurationErrorHandler('"Product code" not defined');
      return [];
    }

    const sliceProductCode = this.slice.product_code;
    const filteredProducts: ProductListItem<Product>[] = [];

    const eligibleSpecialProduct = this.findEligibleSpecialProduct(this.lineCheckEligibleAccess);
    const eligibleSpecialProductFromPrismic = this.productsFromPrismic.find(
      ({ product_code }) => product_code === eligibleSpecialProduct.productCode
    );
    const parentProductFromPrismic = this.productsFromPrismic.find(
      ({ product_code }) => product_code === sliceProductCode
    );

    if (eligibleSpecialProduct && eligibleSpecialProductFromPrismic && parentProductFromPrismic) {
      // Override the following properties to be those of the repeatable zone items, based on eligibility

      if (!eligibleSpecialProduct.redirectForSpecialProductSlice) {
        this.prismicConfigurationErrorHandler(
          `"Redirect (for special product slice)" not defined, inside repeatable zone, for product code ${eligibleSpecialProduct.productCode}`
        );
      }

      if (!eligibleSpecialProduct.productTypeForSpecialProductSlice) {
        this.prismicConfigurationErrorHandler(
          `"Product Type (for special product slice)" not defined, inside repeatable zone, for product code ${eligibleSpecialProduct.productCode}`
        );
      }

      const pcProd = this.productsFromPC.find(prod => prod.code === eligibleSpecialProduct.productCode);
      if (!pcProd) {
        throw Error('Product code does not exist in product catalog : ' + eligibleSpecialProduct.productCode);
      }

      const { promotion } = eligibleSpecialProduct;
      const correctPromotion = await this.productService.getCorrectPromotion(promotion, pcProd);

      const modifiedEligibleSpecialProductFromPrismic = new Product(pcProd, eligibleSpecialProductFromPrismic);
      modifiedEligibleSpecialProductFromPrismic.name = this.productService.getProductName(parentProductFromPrismic);
      modifiedEligibleSpecialProductFromPrismic.product_icon = prismicToImage(parentProductFromPrismic.product_icon);
      modifiedEligibleSpecialProductFromPrismic.short_description = prismicToHtmlString(
        parentProductFromPrismic.short_description
      );

      filteredProducts.push({
        product: modifiedEligibleSpecialProductFromPrismic,
        promotion: correctPromotion,
        lineCheckId,
      });
    }
    return filteredProducts;
  }

  private async fetchEligibleProducts(lineCheckId?: string) {
    if (!this.slice.product_code) {
      this.prismicConfigurationErrorHandler('"Product code" not defined');
      return [];
    }

    const sliceProductCode = this.slice.product_code;
    const productFromPrismic = this.productsFromPrismic.find(({ product_code }) => product_code === sliceProductCode);
    const productFromPC = this.productsFromPC.find(({ code }) => code === sliceProductCode);
    const product = new Product(productFromPC, productFromPrismic);

    const filteredProducts: ProductListItem<Product>[] = [];

    if (productFromPrismic && productFromPC) {
      const correctPromotion = await this.productService.getCorrectPromotion(
        this.slice.promotion as FilledContentRelationshipField,
        productFromPC
      );

      filteredProducts.push({
        product: product,
        promotion: correctPromotion,
        lineCheckId,
      });
    }

    return filteredProducts;
  }

  private async fetchDetailPageAlternativeProducts(lineCheckId?: string) {
    if (!this.slice.lineCheckRepeatableItems.length) {
      this.prismicConfigurationErrorHandler('Alternative product not defined');

      return [];
    }

    const alternativeProduct = this.slice.lineCheckRepeatableItems[0];
    const productFromPC = this.productsFromPC.find(({ code }) => code === alternativeProduct.productCode);
    const productFromPrismic = this.productsFromPrismic.find(({ product_code }) => product_code === productFromPC.code);

    const filteredProducts: ProductListItem<Product>[] = [];

    if (productFromPrismic && productFromPC) {
      const { promotion } = alternativeProduct;
      const correctPromotion = await this.productService.getCorrectPromotion(promotion, productFromPC);
      const product = new Product(productFromPC, productFromPrismic);

      filteredProducts.push({
        product: product,
        promotion: correctPromotion,
        lineCheckId,
      });
    }

    return filteredProducts;
  }

  private async fetchHomePageAlternativeProducts(lineCheckId?: string) {
    const filteredProductsFromPC = this.productsFromPC?.filter(
      ({ code, productSpecClass }) =>
        this.specialProductCodes.includes(code) ||
        this.lineCheckEligibleAccess[productSpecClass?.toLowerCase() as keyof LineCheckEligibleAccess]
    );
    const filteredProducts: ProductListItem<Product>[] = [];

    for (const productPc of filteredProductsFromPC) {
      const productFromPrismic = this.productsFromPrismic.find(({ product_code }) => product_code === productPc.code);

      if (productFromPrismic) {
        const { promotion, featured, local, global, prepaid } =
          this.slice.lineCheckRepeatableItems?.find(({ productCode }) => productCode === productPc.code) || {};

        const correctPromotion = await this.productService.getCorrectPromotion(promotion, productPc);
        const product = new Product(productPc, productFromPrismic);

        filteredProducts.push({
          product: product,
          promotion: correctPromotion,
          lineCheckId,
          featured,
          local,
          global,
          prepaid,
        });
      }
    }

    return filteredProducts.sort(this.productService.sortProductsByDiscount);
  }

  private async fetchProducts() {
    const [productsFromPC, productsFromPrismic] = await Promise.all([
      this.productService.getPCProducts(),
      this.prismicLibService.getProducts(),
    ]);

    this.productsFromPC = productsFromPC;
    this.productsFromPrismic = productsFromPrismic;
    this.specialProductCodes = productsFromPrismic
      .filter(({ special_product_type }) => special_product_type === true)
      .map(({ product_code }) => product_code);
    if (this.specialProductCodes.length === 0) {
      this.prismicConfigurationErrorHandler('No special product types available');
    }
  }

  private findEligibleSpecialProduct(lineCheckEligibleAccess: LineCheckEligibleAccess) {
    const { lineCheckRepeatableItems } = this.slice;
    const { hfcDefined, fiberDefined, mbbDefined } = this.verifySpecialProductItems(lineCheckRepeatableItems);

    let item: LineCheckRepeatableItem;

    if (hfcDefined && lineCheckEligibleAccess.hfc) {
      item = lineCheckRepeatableItems.find(item => item.productTypeForSpecialProductSlice === 'HFC');
    } else if (fiberDefined && lineCheckEligibleAccess.fiber) {
      item = lineCheckRepeatableItems.find(item => item.productTypeForSpecialProductSlice === 'FIBER');
    } else if (mbbDefined && lineCheckEligibleAccess.mbb) {
      item = lineCheckRepeatableItems.find(item => item.productTypeForSpecialProductSlice === 'MBB');
    }

    return item;
  }

  private verifySpecialProductItems(lineCheckRepeatableItems: LineCheckRepeatableItem[]): {
    fiberDefined: boolean;
    mbbDefined: boolean;
    hfcDefined: boolean;
  } {
    // Verifies the list of the Repeatable Zone items in Prismic
    // It adds an extra safety check to make sure that the Prismic values are correctly set

    return {
      fiberDefined: lineCheckRepeatableItems.some(item => item.productTypeForSpecialProductSlice === 'FIBER'),
      hfcDefined: lineCheckRepeatableItems.some(item => item.productTypeForSpecialProductSlice === 'HFC'),
      mbbDefined: lineCheckRepeatableItems.some(item => item.productTypeForSpecialProductSlice === 'MBB'),
    };
  }

  private prismicConfigurationErrorHandler(error: string) {
    console.error('Prismic configuration error:', error);
  }
}
